import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import AppModal from "../../../components/AppModal";
import { i18n } from "../../../translate/i18n";
import { fetchTransactionDetail } from "../hooks/useAsaasTransactions";

function formatDate(value) {
	if (!value) return "—";
	return new Date(value).toLocaleString("pt-BR");
}

function formatCurrency(value) {
	return new Intl.NumberFormat("pt-BR", {
		style: "currency",
		currency: "BRL",
	}).format(Number(value) || 0);
}

function DetailRow({ label, children }) {
	return (
		<div className="flex flex-col gap-1 border-b border-default-200 py-3 last:border-b-0 dark:border-default-700 sm:flex-row sm:items-start sm:justify-between">
			<span className="text-sm font-medium text-default-500">{label}</span>
			<span className="text-sm text-default-800 dark:text-default-100 sm:max-w-[65%] sm:text-right">
				{children}
			</span>
		</div>
	);
}

export default function TransactionDetailModal({ open, transactionId, onClose }) {
	const [loading, setLoading] = useState(false);
	const [detail, setDetail] = useState(null);

	useEffect(() => {
		if (!open || !transactionId) {
			setDetail(null);
			return;
		}

		let cancelled = false;
		setLoading(true);
		fetchTransactionDetail(transactionId)
			.then((data) => {
				if (!cancelled) setDetail(data);
			})
			.catch(() => {
				if (!cancelled) {
					toast.error(i18n.t("transactions.detail.loadError"));
					onClose();
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, transactionId, onClose]);

	const handleCopyPix = async () => {
		if (!detail?.pixPayload) return;
		try {
			await navigator.clipboard.writeText(detail.pixPayload);
			toast.success(i18n.t("transactions.detail.copyPixSuccess"));
		} catch {
			toast.error(i18n.t("transactions.detail.copyPixError"));
		}
	};

	const isPending = detail && !["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes(
		String(detail.status || "").toUpperCase()
	);

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={i18n.t("transactions.detail.title")}
			subtitle={detail?.paymentId || ""}
			footer={
				<div className="flex flex-wrap justify-end gap-2">
					{isPending && detail?.pixPayload ? (
						<button
							type="button"
							className="btn border border-default-200 text-default-700 dark:border-default-600"
							onClick={handleCopyPix}
						>
							{i18n.t("transactions.detail.copyPix")}
						</button>
					) : null}
					<button type="button" className="btn bg-primary text-white" onClick={onClose}>
						{i18n.t("transactions.detail.close")}
					</button>
				</div>
			}
		>
			{loading ? (
				<p className="m-0 text-sm text-default-500">{i18n.t("transactions.loading")}</p>
			) : detail ? (
				<div>
					<DetailRow label={i18n.t("transactions.columns.contact")}>
						{detail.contact?.name || "—"}
						{detail.contact?.number ? ` · ${detail.contact.number}` : ""}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.columns.amount")}>
						{formatCurrency(detail.valor)}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.columns.status")}>
						{detail.status}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.columns.date")}>
						{formatDate(detail.createdAt)}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.columns.dueDate")}>
						{formatDate(detail.dueDate)}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.columns.paidAt")}>
						{formatDate(detail.paidAt)}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.detail.receivedValue")}>
						{detail.receivedValue != null
							? formatCurrency(detail.receivedValue)
							: "—"}
					</DetailRow>
					<DetailRow label={i18n.t("transactions.columns.paymentId")}>
						<span className="font-mono text-xs break-all">{detail.paymentId}</span>
					</DetailRow>
					{detail.pixPayload ? (
						<div className="mt-4 rounded-lg border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900/30">
							<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("transactions.detail.pixCode")}
							</p>
							<p className="m-0 break-all font-mono text-xs text-default-700 dark:text-default-200">
								{detail.pixPayload}
							</p>
						</div>
					) : null}
				</div>
			) : null}
		</AppModal>
	);
}
